import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { AccessAmministratore, AccessPrivilegio } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { colonneStatoAccount, etichettaTipoIdentita } from './comuni'

/**
 * «Privilegi»: who is administrator of what. On top, the **system
 * administrators** one per person — the list the Garante's measure asks for —
 * with how many systems, since when, the last privileged access and whether
 * the privilege is dormant (a right nobody uses is a right to take away).
 * Below, every privilege on every system, the ended ones on request.
 */

export function etichettaAmbito(k: AccessPrivilegio['scope_kind']): string {
  return ({
    local_admin: t('amministratore locale'),
    domain_group: t('gruppo privilegiato di dominio'),
    entra_role: t('ruolo di Entra ID'),
    device_admin: t('amministratore di apparato'),
    sudo: t('sudo'),
  } as Record<string, string>)[k] ?? k
}

export function VistaPrivilegi() {
  const [tutti, setTutti] = useState(false)
  const dati = useAsync(() => api.accessPrivileges(tutti), [tutti])
  const d = dati.data
  if (dati.loading && !d) return <Spinner />
  if (!d) return <ErrorBanner error={dati.error} />
  const fonti = d.privileges.length === 0
  return (
    <>
      <Card title={t('Amministratori di sistema')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Una riga per persona con almeno un privilegio attivo: su quanti sistemi, con quali tipi di privilegio, da quando, l’ultimo accesso privilegiato. «Dormiente» vuol dire nessun accesso privilegiato oltre la soglia delle impostazioni: un privilegio che nessuno usa è un privilegio da togliere.')}
        </p>
        {d.administrators.length === 0 ? (
          <EmptyState title={t('Nessun amministratore, finora.')} hint={t('Gli amministratori locali arrivano dall’inventario dei sistemi (SSH, WinRM, agente) entro un’ora; i gruppi di dominio dai domain controller, i ruoli da Entra ID.')} />
        ) : (
          <DataTable rows={d.administrators} rowKey={(a) => a.identity_id} conteggio={{ nome: t('amministratori') }} columns={[
            { key: 'display_name', header: t('Persona'), value: (a) => a.display_name, render: (a) => (
              <><Link to={`/access/people/${a.identity_id}`}><strong>{a.display_name}</strong></Link><br /><small className="muted">{etichettaTipoIdentita(a.kind)}</small></>
            ) },
            { key: 'systems', header: t('Sistemi'), type: 'number', value: (a) => a.systems,
              render: (a) => a.systems_via_groups ? <span title={t('attraverso: {gruppi}', { gruppi: (a.via_groups ?? []).join(', ') })}>{a.systems} <span className="muted">· {t('{n} tramite gruppo', { n: a.systems_via_groups })}</span></span> : a.systems },
            { key: 'scopes', header: t('Privilegi'), value: (a) => a.scopes.map(etichettaAmbito).join(', '), render: (a) => (
              <span title={a.scope_names.join(', ')}>{a.scopes.map(etichettaAmbito).join(', ')}{a.eligible_only && <> · <span className="muted">{t('solo idoneo')}</span></>}</span>
            ) },
            { key: 'since', header: t('Da'), type: 'date', value: (a) => a.since, render: (a) => formatDateTime(a.since) },
            { key: 'last_privileged_logon', header: t('Ultimo accesso privilegiato'), type: 'date', value: (a) => a.last_privileged_logon ?? '', render: (a) => (
              <>{a.last_privileged_logon ? formatDateTime(a.last_privileged_logon) : <span className="muted">{t('mai visto')}</span>}{a.dormant && <> <span className="badge badge--warn">{t('dormiente')}</span></>}</>
            ) },
            ...colonneStatoAccount<AccessAmministratore>(),
            { key: 'accounts', header: t('Account'), value: (a) => a.accounts.join(' '), render: (a) => <small className="mono muted">{a.accounts.join(', ')}</small>, hidden: true },
          ]} />
        )}
      </Card>
      <Card title={t('Privilegi per sistema')}>
        <label className="checkbox" style={{ marginBottom: 8, display: 'inline-flex' }}>
          <input type="checkbox" checked={tutti} onChange={(e) => setTutti(e.target.checked)} />{' '}{t('anche quelli cessati')}
        </label>
        {fonti ? <EmptyState title={t('Nessun privilegio letto finora.')} /> : (
          <DataTable rows={d.privileges} rowKey={(r) => r.id} conteggio={{ nome: t('privilegi') }} columns={[
            { key: 'scope_name', header: t('Sistema o ambito'), value: (r) => r.ci_name ?? r.scope_name, render: (r) => (
              r.ci_id ? <Link to={`/access/events?ci=${r.ci_id}&periodo=30d&privilegiati=1`}>{r.ci_name ?? r.scope_name}</Link> : r.scope_name
            ) },
            { key: 'scope_kind', header: t('Tipo'), filter: 'select', value: (r) => etichettaAmbito(r.scope_kind) },
            { key: 'account', header: t('Account o gruppo'), mono: true, value: (r) => r.account, render: (r) => (
              r.identity_id ? <Link to={`/access/people/${r.identity_id}`}>{r.account}</Link> : <span title={t('Un gruppo: i suoi membri arrivano dai domain controller.')}>{r.account} <span className="muted">({t('gruppo')})</span></span>
            ) },
            { key: 'active', header: t('Stato'), filter: 'select', value: (r) => (r.active ? (r.eligible ? t('idoneo') : t('attivo')) : t('cessato')) },
            { key: 'first_seen', header: t('Da'), type: 'date', value: (r) => r.first_seen ?? '', render: (r) => formatDateTime(r.first_seen) },
            { key: 'ended_at', header: t('Fino a'), type: 'date', value: (r) => r.ended_at ?? '', render: (r) => (r.ended_at ? formatDateTime(r.ended_at) : <span className="muted">—</span>) },
            { key: 'source', header: t('Fonte'), filter: 'select', value: (r) => ({ inventory: t('inventario'), dc: t('domain controller'), entra: t('Entra ID'), syslog: t('syslog') } as Record<string, string>)[r.source] ?? r.source },
          ]} />
        )}
      </Card>
    </>
  )
}
